/**
 * CompanionStepathonCard — shows active step-a-thon events inside the
 * Companion App home tab. Each card surfaces the event's live total +
 * a tap-to-open link straight to /stepathon/:slug for quick logging.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API from "@/lib/api";
import { Activity, ChevronRight, Gift } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CompanionStepathonCard() {
  const [events, setEvents] = useState([]);
  const [points, setPoints] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${BACKEND}/api/stepathon/events?status=active`)
      .then((r) => setEvents(r.data.events || []))
      .catch(() => setEvents([]));
    API.get("/stepathon/rewards/me")
      .then((r) => setPoints(r.data))
      .catch(() => setPoints(null));
  }, []);

  if (events.length === 0 && !points) return null;

  return (
    <div
      data-testid="companion-stepathon-card"
      className="rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 p-4 text-white shadow"
    >
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          <Activity className="w-4 h-4" />
          <span className="text-[11px] uppercase tracking-wider font-bold opacity-95">
            Step-a-Thon · active
          </span>
        </div>
        <button
          onClick={() => navigate("/stepathon")}
          className="text-[11px] opacity-90 hover:opacity-100"
          data-testid="companion-stepathon-all-btn"
        >
          See all <ChevronRight className="w-3 h-3 inline" />
        </button>
      </div>
      <div className="mt-2 space-y-2">
        {events.slice(0, 2).map((ev) => (
          <button
            key={ev.slug}
            onClick={() => navigate(`/stepathon/${ev.slug}`)}
            data-testid={`companion-stepathon-${ev.slug}`}
            className="w-full text-left rounded-xl bg-white/10 hover:bg-white/15 transition p-3"
          >
            <div className="font-bold text-sm">{ev.name}</div>
            <div className="flex items-center justify-between mt-1 text-[11px] opacity-90">
              <span className="tabular-nums">
                {ev.total_steps.toLocaleString()} steps · {ev.participant_count.toLocaleString()} walkers
              </span>
              <span className="font-semibold">
                {ev.percent_to_goal.toFixed(2)}% of goal →
              </span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-black/20 overflow-hidden">
              <div className="h-full bg-yellow-300/90"
                   style={{ width: `${Math.min(100, ev.percent_to_goal)}%` }} />
            </div>
          </button>
        ))}
        {points && (
          <button
            onClick={() => navigate("/stepathon/rewards")}
            data-testid="companion-stepathon-rewards"
            className="w-full text-left rounded-xl bg-white/10 hover:bg-white/15 transition p-3 flex items-center justify-between"
          >
            <div className="inline-flex items-center gap-2">
              <Gift className="w-4 h-4 text-yellow-200" />
              <div>
                <div className="text-[11px] uppercase tracking-wider opacity-90 font-bold">
                  My rewards
                </div>
                <div className="font-bold text-sm tabular-nums">
                  {points.points_available.toLocaleString()} pts available
                </div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 opacity-80" />
          </button>
        )}
      </div>
    </div>
  );
}
